'use client';

// src/utils.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/button.tsx
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { jsx } from "react/jsx-runtime";
var buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        brand: "bg-orb-gold text-white hover:bg-orb-gold/90 transition-colors",
        brandSecondary: "bg-orb-teal text-white hover:bg-orb-teal/90 transition-colors"
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx(Comp, { className: cn(buttonVariants({ variant, size, className })), ref, ...props });
  }
);
Button.displayName = "Button";

// src/input.tsx
import * as React2 from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var Input = React2.forwardRef(
  ({ className, type, ...props }, ref) => {
    return /* @__PURE__ */ jsx2(
      "input",
      {
        type,
        className: cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        ),
        ref,
        ...props
      }
    );
  }
);
Input.displayName = "Input";

// src/textarea.tsx
import * as React3 from "react";
import { jsx as jsx3 } from "react/jsx-runtime";
var Textarea = React3.forwardRef(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx3(
      "textarea",
      {
        className: cn(
          "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        ),
        ref,
        ...props
      }
    );
  }
);
Textarea.displayName = "Textarea";

// src/card.tsx
import * as React4 from "react";
import { jsx as jsx4 } from "react/jsx-runtime";
var Card = React4.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx4(
    "div",
    {
      ref,
      className: cn("rounded-lg border bg-card text-card-foreground shadow-sm", className),
      ...props
    }
  )
);
Card.displayName = "Card";
var CardHeader = React4.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx4("div", { ref, className: cn("flex flex-col space-y-1.5 p-6", className), ...props })
);
CardHeader.displayName = "CardHeader";
var CardTitle = React4.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx4(
    "div",
    {
      ref,
      className: cn("text-2xl font-semibold leading-none tracking-tight", className),
      ...props
    }
  )
);
CardTitle.displayName = "CardTitle";
var CardDescription = React4.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx4("div", { ref, className: cn("text-sm text-muted-foreground", className), ...props })
);
CardDescription.displayName = "CardDescription";
var CardContent = React4.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx4("div", { ref, className: cn("p-6 pt-0", className), ...props })
);
CardContent.displayName = "CardContent";
var CardFooter = React4.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx4("div", { ref, className: cn("flex items-center p-6 pt-0", className), ...props })
);
CardFooter.displayName = "CardFooter";

// src/badge.tsx
import { cva as cva2 } from "class-variance-authority";
import { jsx as jsx5 } from "react/jsx-runtime";
var badgeVariants = cva2(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive: "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        success: "border-transparent bg-green-500 text-white hover:bg-green-500/80",
        warning: "border-transparent bg-yellow-500 text-white hover:bg-yellow-500/80"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Badge({ className, variant, ...props }) {
  return /* @__PURE__ */ jsx5("div", { className: cn(badgeVariants({ variant }), className), ...props });
}

// src/label.tsx
import * as React5 from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cva as cva3 } from "class-variance-authority";
import { jsx as jsx6 } from "react/jsx-runtime";
var labelVariants = cva3(
  "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
);
var Label = React5.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx6(LabelPrimitive.Root, { ref, className: cn(labelVariants(), className), ...props }));
Label.displayName = LabelPrimitive.Root.displayName;

// src/separator.tsx
import * as React6 from "react";
import * as SeparatorPrimitive from "@radix-ui/react-separator";
import { jsx as jsx7 } from "react/jsx-runtime";
var Separator = React6.forwardRef(({ className, orientation = "horizontal", decorative = true, ...props }, ref) => /* @__PURE__ */ jsx7(
  SeparatorPrimitive.Root,
  {
    ref,
    decorative,
    orientation,
    className: cn(
      "shrink-0 bg-border",
      orientation === "horizontal" ? "h-[1px] w-full" : "h-full w-[1px]",
      className
    ),
    ...props
  }
));
Separator.displayName = SeparatorPrimitive.Root.displayName;

// src/skeleton.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
function Skeleton({ className, ...props }) {
  return /* @__PURE__ */ jsx8("div", { className: cn("animate-pulse rounded-md bg-muted", className), ...props });
}

// src/form-field.tsx
import { jsx as jsx9, jsxs } from "react/jsx-runtime";
function FormField({ label, htmlFor, children, error, className }) {
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-2", className), children: [
    /* @__PURE__ */ jsx9(Label, { htmlFor, children: label }),
    children,
    error && /* @__PURE__ */ jsx9("p", { className: "text-sm text-destructive", children: error })
  ] });
}

// src/password-input.tsx
import * as React7 from "react";
import { jsx as jsx10, jsxs as jsxs2 } from "react/jsx-runtime";
var PasswordInput = React7.forwardRef(({ className, iconClassName, ...props }, ref) => {
  const [showPassword, setShowPassword] = React7.useState(false);
  return /* @__PURE__ */ jsxs2("div", { className: "relative", children: [
    /* @__PURE__ */ jsx10(
      Input,
      {
        type: showPassword ? "text" : "password",
        className: cn("pr-10", className),
        ref,
        ...props
      }
    ),
    /* @__PURE__ */ jsx10(
      "button",
      {
        type: "button",
        onClick: () => setShowPassword(!showPassword),
        className: cn(
          "absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground",
          iconClassName
        ),
        tabIndex: -1,
        "aria-label": showPassword ? "Hide password" : "Show password",
        children: showPassword ? /* @__PURE__ */ jsxs2(
          "svg",
          {
            xmlns: "http://www.w3.org/2000/svg",
            width: "16",
            height: "16",
            viewBox: "0 0 24 24",
            fill: "none",
            stroke: "currentColor",
            strokeWidth: "2",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            children: [
              /* @__PURE__ */ jsx10("path", { d: "M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" }),
              /* @__PURE__ */ jsx10("path", { d: "M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" }),
              /* @__PURE__ */ jsx10("line", { x1: "1", y1: "1", x2: "23", y2: "23" }),
              /* @__PURE__ */ jsx10("path", { d: "M14.12 14.12a3 3 0 1 1-4.24-4.24" })
            ]
          }
        ) : /* @__PURE__ */ jsxs2(
          "svg",
          {
            xmlns: "http://www.w3.org/2000/svg",
            width: "16",
            height: "16",
            viewBox: "0 0 24 24",
            fill: "none",
            stroke: "currentColor",
            strokeWidth: "2",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            children: [
              /* @__PURE__ */ jsx10("path", { d: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" }),
              /* @__PURE__ */ jsx10("circle", { cx: "12", cy: "12", r: "3" })
            ]
          }
        )
      }
    )
  ] });
});
PasswordInput.displayName = "PasswordInput";

// src/verification-code-input.tsx
import * as React8 from "react";
import { jsx as jsx11 } from "react/jsx-runtime";
var VerificationCodeInput = React8.forwardRef(({ className, length = 6, ...props }, ref) => {
  return /* @__PURE__ */ jsx11(
    Input,
    {
      type: "text",
      inputMode: "numeric",
      placeholder: "0".repeat(length),
      maxLength: length,
      className: cn("text-center text-lg tracking-widest", className),
      ref,
      ...props
    }
  );
});
VerificationCodeInput.displayName = "VerificationCodeInput";

// src/submit-button.tsx
import * as React9 from "react";
import { Fragment, jsx as jsx12, jsxs as jsxs3 } from "react/jsx-runtime";
var SubmitButton = React9.forwardRef(
  ({ isSubmitting, loadingText, children, className, disabled, ...props }, ref) => {
    return /* @__PURE__ */ jsx12(
      Button,
      {
        type: "submit",
        className: cn("w-full", className),
        disabled: disabled || isSubmitting,
        ref,
        ...props,
        children: isSubmitting ? /* @__PURE__ */ jsxs3(Fragment, { children: [
          /* @__PURE__ */ jsx12(
            "svg",
            {
              className: "mr-2 h-4 w-4 animate-spin",
              xmlns: "http://www.w3.org/2000/svg",
              width: "24",
              height: "24",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              strokeWidth: "2",
              strokeLinecap: "round",
              strokeLinejoin: "round",
              children: /* @__PURE__ */ jsx12("path", { d: "M21 12a9 9 0 1 1-6.219-8.56" })
            }
          ),
          loadingText || "Loading..."
        ] }) : children
      }
    );
  }
);
SubmitButton.displayName = "SubmitButton";

// src/auth-card.tsx
import { jsx as jsx13, jsxs as jsxs4 } from "react/jsx-runtime";
function AuthCard({ title, description, children, className, headerClassName }) {
  return /* @__PURE__ */ jsxs4(Card, { className: cn("border-border/60", className), children: [
    /* @__PURE__ */ jsxs4(CardHeader, { className: headerClassName, children: [
      /* @__PURE__ */ jsx13(CardTitle, { children: title }),
      description && /* @__PURE__ */ jsx13(CardDescription, { children: description })
    ] }),
    /* @__PURE__ */ jsx13(CardContent, { children })
  ] });
}

// src/legal/terms-content.tsx
import { Fragment as Fragment2, jsx as jsx14, jsxs as jsxs5 } from "react/jsx-runtime";
function TermsContent({ showHeading = true, className }) {
  return /* @__PURE__ */ jsxs5("div", { className: cn("w-full", className), children: [
    showHeading && /* @__PURE__ */ jsxs5(Fragment2, { children: [
      /* @__PURE__ */ jsx14("h1", { className: "font-display text-4xl md:text-5xl font-bold tracking-tight text-center", children: "Terms of Service" }),
      /* @__PURE__ */ jsx14("p", { className: "mt-6 text-muted-foreground text-center max-w-2xl mx-auto", children: "These Terms explain the rules for using Legacy Orb. They cover account use, user content, AI-generated outputs, payments, digital legacy features, dispute resolution, and other important legal rights and responsibilities." })
    ] }),
    /* @__PURE__ */ jsxs5(
      "div",
      {
        className: cn(
          "prose prose-lg prose-neutral prose-headings:font-display prose-h2:text-2xl prose-h3:text-xl prose-a:text-orb-gold max-w-none",
          showHeading && "mt-12"
        ),
        children: [
          /* @__PURE__ */ jsxs5("p", { children: [
            /* @__PURE__ */ jsx14("strong", { children: "Effective Date:" }),
            " January 1st, 2026",
            /* @__PURE__ */ jsx14("br", {}),
            /* @__PURE__ */ jsx14("strong", { children: "Last Updated:" }),
            " March 18th, 2026"
          ] }),
          /* @__PURE__ */ jsxs5("p", { children: [
            "Welcome to Legacy Orb. These Terms of Service (\u201CTerms\u201D) govern your access to and use of the Legacy Orb website, mobile applications, and related products and services (collectively, the \u201CServices\u201D) provided by ",
            /* @__PURE__ */ jsx14("strong", { children: "Legacy Orb LLC" }),
            " ",
            "(\u201CLegacy Orb,\u201D \u201CCompany,\u201D \u201Cwe,\u201D \u201Cour,\u201D or \u201Cus\u201D)."
          ] }),
          /* @__PURE__ */ jsx14("p", { children: "By accessing or using the Services, you agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, do not use the Services." }),
          /* @__PURE__ */ jsx14("h3", { children: "1. Who We Are" }),
          /* @__PURE__ */ jsx14("p", { children: "Legacy Orb provides tools and services designed to help users capture, preserve, organize, and transform personal stories, memories, recordings, and related content into structured legacy materials, including AI-assisted outputs." }),
          /* @__PURE__ */ jsx14("h3", { children: "2. Eligibility" }),
          /* @__PURE__ */ jsx14("p", { children: "You may use the Services only if:" }),
          /* @__PURE__ */ jsxs5("ul", { children: [
            /* @__PURE__ */ jsx14("li", { children: "you are at least 18 years old, or otherwise have the legal authority to enter into these Terms," }),
            /* @__PURE__ */ jsx14("li", { children: "you have the legal capacity to form a binding agreement, and" }),
            /* @__PURE__ */ jsx14("li", { children: "your use of the Services is not prohibited under applicable law." })
          ] }),
          /* @__PURE__ */ jsx14("p", { children: "By using the Services, you represent and warrant that you meet these requirements." }),
          /* @__PURE__ */ jsx14("h3", { children: "3. Acceptance of Terms" }),
          /* @__PURE__ */ jsx14("p", { children: "By accessing or using the Services, you agree to these Terms and our Privacy Policy. If you are using the Services on behalf of another person, organization, or entity, you represent that you have authority to bind that party to these Terms." }),
          /* @__PURE__ */ jsx14("h3", { children: "4. Description of the Services" }),
          /* @__PURE__ */ jsx14("p", { children: "The Services may include tools for:" }),
          /* @__PURE__ */ jsxs5("ul", { children: [
            /* @__PURE__ */ jsx14("li", { children: "capturing written, audio, visual, and other personal content," }),
            /* @__PURE__ */ jsx14("li", { children: "organizing memories, life stories, and related materials," }),
            /* @__PURE__ */ jsx14("li", { children: "generating AI-assisted summaries, narratives, scripts, and structured outputs," }),
            /* @__PURE__ */ jsx14("li", { children: "storing and preserving legacy-related content." })
          ] }),
          /* @__PURE__ */ jsx14("p", { children: "We may modify, update, suspend, or discontinue any part of the Services at any time, with or without notice, to the extent permitted by law." }),
          /* @__PURE__ */ jsx14("h3", { children: "5. User Accounts" }),
          /* @__PURE__ */ jsx14("p", { children: "To access certain features, you may need to create an account. You are responsible for:" }),
          /* @__PURE__ */ jsxs5("ul", { children: [
            /* @__PURE__ */ jsx14("li", { children: "maintaining the confidentiality of your login credentials," }),
            /* @__PURE__ */ jsx14("li", { children: "all activities that occur under your account," }),
            /* @__PURE__ */ jsx14("li", { children: "providing accurate, current, and complete information." })
          ] }),
          /* @__PURE__ */ jsx14("p", { children: "You agree to notify us promptly of any unauthorized use of your account or any security breach involving your account." }),
          /* @__PURE__ */ jsx14("h3", { children: "6. Your Content" }),
          /* @__PURE__ */ jsx14("p", { children: "You retain ownership of the content you submit, upload, record, transmit, or otherwise provide through the Services (\u201CUser Content\u201D), including text, audio, photos, videos, personal narratives, and similar materials." }),
          /* @__PURE__ */ jsx14("h4", { children: "6.1 License to Legacy Orb" }),
          /* @__PURE__ */ jsx14("p", { children: "By submitting User Content through the Services, you grant Legacy Orb a worldwide, non-exclusive, royalty-free, fully paid, transferable, and sublicensable license to:" }),
          /* @__PURE__ */ jsxs5("ul", { children: [
            /* @__PURE__ */ jsx14("li", { children: "host, store, reproduce, and display your User Content," }),
            /* @__PURE__ */ jsx14("li", { children: "modify, adapt, format, and create derivative works from your User Content," }),
            /* @__PURE__ */ jsx14("li", { children: "analyze, classify, structure, and transform User Content into metadata, tags, timelines, and other derived data," }),
            /* @__PURE__ */ jsx14("li", { children: "generate AI-assisted outputs based on your User Content," }),
            /* @__PURE__ */ jsx14("li", { children: "use User Content and related derived data as reasonably necessary to operate, maintain, improve, and provide the Services." })
          ] }),
          /* @__PURE__ */ jsx14("p", { children: "The license above is limited to the operation, support, improvement, and delivery of the Services. It does not give Legacy Orb the right to sell your User Content as standalone content." }),
          /* @__PURE__ */ jsx14("h3", { children: "7. AI Processing and Outputs" }),
          /* @__PURE__ */ jsx14("p", { children: "The Services may use artificial intelligence, machine learning systems, and third-party AI providers to process User Content and generate outputs." }),
          /* @__PURE__ */ jsx14("p", { children: "By using the Services, you acknowledge and agree that:" }),
          /* @__PURE__ */ jsxs5("ul", { children: [
            /* @__PURE__ */ jsx14("li", { children: "AI-generated content may be inaccurate, incomplete, subjective, or inconsistent," }),
            /* @__PURE__ */ jsx14("li", { children: "AI outputs are generated automatically and may require human review," }),
            /* @__PURE__ */ jsx14("li", { children: "Legacy Orb does not guarantee the factual accuracy, completeness, or reliability of any AI-generated output," }),
            /* @__PURE__ */ jsx14("li", { children: "AI outputs are provided for informational, creative, and organizational purposes only and do not constitute legal, medical, financial, mental health, or other professional advice." })
          ] }),
          /* @__PURE__ */ jsx14("h3", { children: "8. Your Responsibilities and Representations" }),
          /* @__PURE__ */ jsx14("p", { children: "You represent and warrant that:" }),
          /* @__PURE__ */ jsxs5("ul", { children: [
            /* @__PURE__ */ jsx14("li", { children: "you own or control all necessary rights in the User Content you submit," }),
            /* @__PURE__ */ jsx14("li", { children: "your User Content does not violate any law or regulation," }),
            /* @__PURE__ */ jsx14("li", { children: "your User Content does not infringe or misappropriate any intellectual property, privacy, publicity, or other rights," }),
            /* @__PURE__ */ jsx14("li", { children: "your User Content is not unlawful, fraudulent, abusive, defamatory, or misleading," }),
            /* @__PURE__ */ jsx14("li", { children: "your use of the Services complies with these Terms and applicable law." })
          ] }),
          /* @__PURE__ */ jsx14("h3", { children: "9. Prohibited Uses" }),
          /* @__PURE__ */ jsx14("p", { children: "You agree not to:" }),
          /* @__PURE__ */ jsxs5("ul", { children: [
            /* @__PURE__ */ jsx14("li", { children: "use the Services for any unlawful or unauthorized purpose," }),
            /* @__PURE__ */ jsx14("li", { children: "upload or transmit content that is harmful, abusive, infringing, threatening, or otherwise objectionable," }),
            /* @__PURE__ */ jsx14("li", { children: "interfere with or disrupt the integrity, performance, or security of the Services," }),
            /* @__PURE__ */ jsx14("li", { children: "attempt to reverse engineer, decompile, scrape, or exploit the Services except as expressly permitted by law," }),
            /* @__PURE__ */ jsx14("li", { children: "use the Services to develop competing products or services in violation of applicable law," }),
            /* @__PURE__ */ jsx14("li", { children: "bypass access controls, security measures, or usage limits." })
          ] }),
          /* @__PURE__ */ jsx14("h3", { children: "10. Content Moderation and Enforcement" }),
          /* @__PURE__ */ jsx14("p", { children: "Legacy Orb reserves the right, but not the obligation, to:" }),
          /* @__PURE__ */ jsxs5("ul", { children: [
            /* @__PURE__ */ jsx14("li", { children: "review, monitor, remove, restrict, or refuse User Content," }),
            /* @__PURE__ */ jsx14("li", { children: "investigate suspected violations of these Terms," }),
            /* @__PURE__ */ jsx14("li", { children: "suspend or terminate access to the Services," }),
            /* @__PURE__ */ jsx14("li", { children: "comply with legal obligations, court orders, regulatory requests, or law enforcement requests." })
          ] }),
          /* @__PURE__ */ jsx14("p", { children: "We may take these actions in our sole discretion where reasonably necessary to protect the Services, users, third parties, or Legacy Orb." }),
          /* @__PURE__ */ jsx14("h3", { children: "11. Third-Party Services" }),
          /* @__PURE__ */ jsx14("p", { children: "The Services may depend on or integrate with third-party products, infrastructure, software, AI systems, cloud hosting, analytics, payment processors, or other third-party services (\u201CThird-Party Services\u201D)." }),
          /* @__PURE__ */ jsx14("p", { children: "Legacy Orb is not responsible for:" }),
          /* @__PURE__ */ jsxs5("ul", { children: [
            /* @__PURE__ */ jsx14("li", { children: "the availability, performance, or reliability of Third-Party Services," }),
            /* @__PURE__ */ jsx14("li", { children: "acts, omissions, or failures of third-party providers," }),
            /* @__PURE__ */ jsx14("li", { children: "changes made by third-party providers that affect the Services." })
          ] }),
          /* @__PURE__ */ jsx14("p", { children: "Your use of certain Third-Party Services may also be subject to separate third-party terms and policies." }),
          /* @__PURE__ */ jsx14("h3", { children: "12. Fees and Payments" }),
          /* @__PURE__ */ jsx14("p", { children: "Certain features or services may require payment. If applicable:" }),
          /* @__PURE__ */ jsxs5("ul", { children: [
            /* @__PURE__ */ jsx14("li", { children: "you agree to pay all disclosed fees and charges," }),
            /* @__PURE__ */ jsx14("li", { children: "all fees are non-refundable except as required by law or expressly stated otherwise," }),
            /* @__PURE__ */ jsx14("li", { children: "we may change pricing with reasonable notice," }),
            /* @__PURE__ */ jsx14("li", { children: "failure to pay may result in suspension or loss of access to paid features." })
          ] }),
          /* @__PURE__ */ jsx14("h3", { children: "13. Beta and Experimental Features" }),
          /* @__PURE__ */ jsxs5("p", { children: [
            "Some features may be identified as ",
            /* @__PURE__ */ jsx14("strong", { children: "beta" }),
            ", ",
            /* @__PURE__ */ jsx14("strong", { children: "pilot" }),
            ",",
            " ",
            /* @__PURE__ */ jsx14("strong", { children: "preview" }),
            ", or ",
            /* @__PURE__ */ jsx14("strong", { children: "experimental" }),
            " features (\u201CBeta Services\u201D)."
          ] }),
          /* @__PURE__ */ jsx14("p", { children: "You understand and agree that Beta Services:" }),
          /* @__PURE__ */ jsxs5("ul", { children: [
            /* @__PURE__ */ jsx14("li", { children: "may be incomplete, unstable, or contain errors," }),
            /* @__PURE__ */ jsx14("li", { children: "may change materially or be discontinued at any time," }),
            /* @__PURE__ */ jsx14("li", { children: "are provided \u201Cas is\u201D and without warranties of any kind," }),
            /* @__PURE__ */ jsx14("li", { children: "may not be suitable for production, archival, or mission-critical use." })
          ] }),
          /* @__PURE__ */ jsx14("h3", { children: "14. Digital Legacy and Posthumous Access" }),
          /* @__PURE__ */ jsx14("p", { children: "Legacy Orb may offer features related to digital legacy planning, legacy contacts, memorialization, archival preservation, or posthumous access preferences." }),
          /* @__PURE__ */ jsx14("p", { children: "Subject to applicable law and satisfactory verification, Legacy Orb may:" }),
          /* @__PURE__ */ jsxs5("ul", { children: [
            /* @__PURE__ */ jsx14("li", { children: "allow users to designate legacy contacts or similar authorized persons," }),
            /* @__PURE__ */ jsx14("li", { children: "grant limited access to eligible individuals after death or incapacity," }),
            /* @__PURE__ */ jsx14("li", { children: "preserve or archive content for legacy-related purposes," }),
            /* @__PURE__ */ jsx14("li", { children: "deny or restrict access where authorization is unclear, disputed, or legally insufficient." })
          ] }),
          /* @__PURE__ */ jsx14("p", { children: "Legacy Orb is not responsible for disputes among heirs, beneficiaries, family members, executors, or other parties concerning access to content or legacy rights." }),
          /* @__PURE__ */ jsx14("h3", { children: "15. Intellectual Property Rights in the Services" }),
          /* @__PURE__ */ jsx14("p", { children: "Except for your User Content, the Services and all related software, designs, interfaces, text, graphics, branding, trademarks, logos, features, and underlying technology are owned by Legacy Orb or its licensors and are protected by intellectual property and other applicable laws." }),
          /* @__PURE__ */ jsx14("p", { children: "These Terms do not grant you any ownership rights in the Services. You receive only a limited, revocable, non-exclusive, non-transferable right to use the Services in accordance with these Terms." }),
          /* @__PURE__ */ jsx14("h3", { children: "16. Disclaimer of Warranties" }),
          /* @__PURE__ */ jsxs5("p", { children: [
            "To the fullest extent permitted by law, the Services are provided",
            " ",
            /* @__PURE__ */ jsx14("strong", { children: "\u201Cas is\u201D" }),
            " and ",
            /* @__PURE__ */ jsx14("strong", { children: "\u201Cas available\u201D" }),
            " ",
            "without warranties of any kind, whether express, implied, statutory, or otherwise."
          ] }),
          /* @__PURE__ */ jsx14("p", { children: "Legacy Orb disclaims all warranties, including implied warranties of:" }),
          /* @__PURE__ */ jsxs5("ul", { children: [
            /* @__PURE__ */ jsx14("li", { children: "merchantability," }),
            /* @__PURE__ */ jsx14("li", { children: "fitness for a particular purpose," }),
            /* @__PURE__ */ jsx14("li", { children: "non-infringement," }),
            /* @__PURE__ */ jsx14("li", { children: "accuracy," }),
            /* @__PURE__ */ jsx14("li", { children: "availability," }),
            /* @__PURE__ */ jsx14("li", { children: "reliability," }),
            /* @__PURE__ */ jsx14("li", { children: "uninterrupted or error-free performance." })
          ] }),
          /* @__PURE__ */ jsx14("p", { children: "We do not warrant that the Services will be secure, error-free, always available, or free from harmful components, or that any content or output will be accurate or complete." }),
          /* @__PURE__ */ jsx14("h3", { children: "17. Limitation of Liability" }),
          /* @__PURE__ */ jsx14("p", { children: "To the maximum extent permitted by law:" }),
          /* @__PURE__ */ jsxs5("ul", { children: [
            /* @__PURE__ */ jsx14("li", { children: "Legacy Orb will not be liable for any indirect, incidental, special, consequential, exemplary, or punitive damages, or for any loss of profits, revenues, goodwill, data, use, or other intangible losses," }),
            /* @__PURE__ */ jsxs5("li", { children: [
              "Legacy Orb's total cumulative liability for all claims arising out of or relating to the Services or these Terms will not exceed the greater of:",
              /* @__PURE__ */ jsxs5("ul", { children: [
                /* @__PURE__ */ jsx14("li", { children: "the amount you paid to Legacy Orb for the Services in the 12 months before the claim arose, or" }),
                /* @__PURE__ */ jsxs5("li", { children: [
                  /* @__PURE__ */ jsx14("strong", { children: "$100 USD" }),
                  "."
                ] })
              ] })
            ] })
          ] }),
          /* @__PURE__ */ jsx14("p", { children: "Some jurisdictions do not allow certain limitations of liability, so portions of this section may not apply to you." }),
          /* @__PURE__ */ jsx14("h3", { children: "18. Indemnification" }),
          /* @__PURE__ */ jsx14("p", { children: "You agree to defend, indemnify, and hold harmless Legacy Orb, its affiliates, officers, directors, employees, contractors, agents, licensors, and service providers from and against any claims, liabilities, damages, losses, judgments, costs, and expenses, including reasonable attorneys' fees, arising out of or related to:" }),
          /* @__PURE__ */ jsxs5("ul", { children: [
            /* @__PURE__ */ jsx14("li", { children: "your use of the Services," }),
            /* @__PURE__ */ jsx14("li", { children: "your User Content," }),
            /* @__PURE__ */ jsx14("li", { children: "your violation of these Terms," }),
            /* @__PURE__ */ jsx14("li", { children: "your violation of any rights of another person or entity," }),
            /* @__PURE__ */ jsx14("li", { children: "your violation of applicable law." })
          ] }),
          /* @__PURE__ */ jsx14("h3", { children: "19. Dispute Resolution and Arbitration" }),
          /* @__PURE__ */ jsx14("p", { children: /* @__PURE__ */ jsx14("strong", { children: "Please read this section carefully. It affects your legal rights." }) }),
          /* @__PURE__ */ jsx14("h4", { children: "19.1 Binding Arbitration" }),
          /* @__PURE__ */ jsxs5("p", { children: [
            "Except where prohibited by law, any dispute, claim, or controversy arising out of or relating to these Terms or the Services will be resolved by binding arbitration administered by the ",
            /* @__PURE__ */ jsx14("strong", { children: "American Arbitration Association (AAA)" }),
            " in accordance with its applicable rules."
          ] }),
          /* @__PURE__ */ jsx14("h4", { children: "19.2 Venue" }),
          /* @__PURE__ */ jsxs5("p", { children: [
            "Unless otherwise required by applicable law, the arbitration will take place in",
            " ",
            /* @__PURE__ */ jsx14("strong", { children: "Virginia, United States" }),
            "."
          ] }),
          /* @__PURE__ */ jsx14("h4", { children: "19.3 Small Claims Exception" }),
          /* @__PURE__ */ jsx14("p", { children: "Either party may bring an eligible individual claim in small claims court instead of arbitration." }),
          /* @__PURE__ */ jsx14("h3", { children: "20. Class Action Waiver" }),
          /* @__PURE__ */ jsx14("p", { children: "To the fullest extent permitted by law, you agree that any dispute will be resolved only on an individual basis and not as part of a class action, consolidated action, collective action, or representative proceeding." }),
          /* @__PURE__ */ jsx14("h3", { children: "21. Jury Trial Waiver" }),
          /* @__PURE__ */ jsx14("p", { children: "To the fullest extent permitted by law, you and Legacy Orb waive any right to a jury trial in any proceeding arising out of or relating to these Terms or the Services." }),
          /* @__PURE__ */ jsx14("h3", { children: "22. Force Majeure" }),
          /* @__PURE__ */ jsx14("p", { children: "Legacy Orb will not be liable for any failure or delay in performance caused by events beyond its reasonable control, including acts of God, natural disasters, war, terrorism, civil unrest, labor disputes, internet outages, infrastructure failures, government actions, or failures of third-party providers." }),
          /* @__PURE__ */ jsx14("h3", { children: "23. Export Control and Compliance" }),
          /* @__PURE__ */ jsx14("p", { children: "You agree not to use, export, re-export, transfer, or make available the Services in violation of any applicable trade, sanctions, or export control laws or regulations." }),
          /* @__PURE__ */ jsx14("h3", { children: "24. Termination" }),
          /* @__PURE__ */ jsx14("p", { children: "We may suspend, restrict, or terminate your access to the Services at any time, with or without notice, if:" }),
          /* @__PURE__ */ jsxs5("ul", { children: [
            /* @__PURE__ */ jsx14("li", { children: "you violate these Terms," }),
            /* @__PURE__ */ jsx14("li", { children: "we are required to do so by law," }),
            /* @__PURE__ */ jsx14("li", { children: "continuing to provide the Services is no longer commercially feasible," }),
            /* @__PURE__ */ jsx14("li", { children: "your use poses risk to the Services, other users, or Legacy Orb." })
          ] }),
          /* @__PURE__ */ jsx14("p", { children: "Upon termination:" }),
          /* @__PURE__ */ jsxs5("ul", { children: [
            /* @__PURE__ */ jsx14("li", { children: "your right to use the Services ends immediately," }),
            /* @__PURE__ */ jsx14("li", { children: "certain provisions of these Terms will survive as described below." })
          ] }),
          /* @__PURE__ */ jsx14("h3", { children: "25. Survival" }),
          /* @__PURE__ */ jsx14("p", { children: "The following sections survive termination to the extent applicable: ownership, licenses, disclaimers, limitations of liability, indemnification, dispute resolution, class action waiver, jury waiver, governing law, and any other provisions that by their nature should survive." }),
          /* @__PURE__ */ jsx14("h3", { children: "26. Assignment" }),
          /* @__PURE__ */ jsx14("p", { children: "You may not assign or transfer these Terms, in whole or in part, without our prior written consent. Legacy Orb may assign or transfer these Terms freely, including in connection with a merger, acquisition, financing, corporate reorganization, or sale of assets." }),
          /* @__PURE__ */ jsx14("h3", { children: "27. Entire Agreement" }),
          /* @__PURE__ */ jsx14("p", { children: "These Terms, together with our Privacy Policy and any other policies or terms expressly incorporated by reference, constitute the entire agreement between you and Legacy Orb regarding the Services and supersede prior or contemporaneous understandings relating to the Services." }),
          /* @__PURE__ */ jsx14("h3", { children: "28. Governing Law" }),
          /* @__PURE__ */ jsxs5("p", { children: [
            "These Terms are governed by the laws of the ",
            /* @__PURE__ */ jsx14("strong", { children: "Commonwealth of Virginia" }),
            ", without regard to its conflict of law principles."
          ] }),
          /* @__PURE__ */ jsx14("h3", { children: "29. Changes to These Terms" }),
          /* @__PURE__ */ jsx14("p", { children: "We may update these Terms from time to time. If we make material changes, we may post the revised Terms on this page and update the \u201CLast Updated\u201D date above. Your continued use of the Services after the updated Terms become effective constitutes your acceptance of the revised Terms." }),
          /* @__PURE__ */ jsx14("h3", { children: "Contact Us" }),
          /* @__PURE__ */ jsx14("p", { children: "If you have questions about these Terms of Service:" }),
          /* @__PURE__ */ jsxs5("p", { children: [
            "Legacy Orb LLC",
            /* @__PURE__ */ jsx14("br", {}),
            /* @__PURE__ */ jsx14("a", { href: "mailto:Info@LegacyOrb.com", children: "Info@LegacyOrb.com" }),
            /* @__PURE__ */ jsx14("br", {}),
            "(703) 679-8707"
          ] })
        ]
      }
    )
  ] });
}

// src/legal/privacy-content.tsx
import { Fragment as Fragment3, jsx as jsx15, jsxs as jsxs6 } from "react/jsx-runtime";
function PrivacyContent({ showHeading = true, className }) {
  return /* @__PURE__ */ jsxs6("div", { className: cn("w-full", className), children: [
    showHeading && /* @__PURE__ */ jsxs6(Fragment3, { children: [
      /* @__PURE__ */ jsx15("h1", { className: "font-display text-4xl md:text-5xl font-bold tracking-tight text-center", children: "Your Stories Are Personal" }),
      /* @__PURE__ */ jsx15("p", { className: "mt-6 text-muted-foreground text-center max-w-2xl mx-auto", children: "Legacy Orb is built to help preserve life stories, memories, and legacy content with care. This Privacy Policy explains what information we collect, how we use it, how AI processing works within the product, and the choices you have regarding your data." }),
      /* @__PURE__ */ jsxs6("div", { className: "mt-12 rounded-2xl border bg-card p-8", children: [
        /* @__PURE__ */ jsx15("h2", { className: "font-display text-3xl md:text-4xl font-bold mb-6", children: "Privacy at a Glance" }),
        /* @__PURE__ */ jsxs6("ul", { className: "space-y-3", children: [
          /* @__PURE__ */ jsxs6("li", { className: "flex items-start gap-3", children: [
            /* @__PURE__ */ jsx15("span", { className: "mt-1.5 h-2 w-2 rounded-full bg-orb-gold flex-shrink-0" }),
            /* @__PURE__ */ jsx15("span", { className: "text-muted-foreground", children: "We collect information you provide directly, including account details and story content." })
          ] }),
          /* @__PURE__ */ jsxs6("li", { className: "flex items-start gap-3", children: [
            /* @__PURE__ */ jsx15("span", { className: "mt-1.5 h-2 w-2 rounded-full bg-orb-gold flex-shrink-0" }),
            /* @__PURE__ */ jsx15("span", { className: "text-muted-foreground", children: "We may process your content using artificial intelligence to organize, analyze, and generate outputs." })
          ] }),
          /* @__PURE__ */ jsxs6("li", { className: "flex items-start gap-3", children: [
            /* @__PURE__ */ jsx15("span", { className: "mt-1.5 h-2 w-2 rounded-full bg-orb-gold flex-shrink-0" }),
            /* @__PURE__ */ jsx15("span", { className: "text-muted-foreground", children: "We may collect technical and usage information to operate and improve the Services." })
          ] }),
          /* @__PURE__ */ jsxs6("li", { className: "flex items-start gap-3", children: [
            /* @__PURE__ */ jsx15("span", { className: "mt-1.5 h-2 w-2 rounded-full bg-orb-gold flex-shrink-0" }),
            /* @__PURE__ */ jsxs6("span", { className: "text-muted-foreground", children: [
              "We do ",
              /* @__PURE__ */ jsx15("strong", { className: "text-foreground", children: "not" }),
              " sell your personal data."
            ] })
          ] }),
          /* @__PURE__ */ jsxs6("li", { className: "flex items-start gap-3", children: [
            /* @__PURE__ */ jsx15("span", { className: "mt-1.5 h-2 w-2 rounded-full bg-orb-gold flex-shrink-0" }),
            /* @__PURE__ */ jsx15("span", { className: "text-muted-foreground", children: "You may request access to, correction of, export of, or deletion of your data, subject to applicable law." })
          ] }),
          /* @__PURE__ */ jsxs6("li", { className: "flex items-start gap-3", children: [
            /* @__PURE__ */ jsx15("span", { className: "mt-1.5 h-2 w-2 rounded-full bg-orb-gold flex-shrink-0" }),
            /* @__PURE__ */ jsx15("span", { className: "text-muted-foreground", children: "We use reasonable administrative, technical, and organizational safeguards to protect your information." })
          ] })
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsxs6(
      "div",
      {
        className: cn(
          "prose prose-lg prose-neutral prose-headings:font-display prose-h2:text-2xl prose-h3:text-xl prose-a:text-orb-gold max-w-none",
          showHeading && "mt-12"
        ),
        children: [
          /* @__PURE__ */ jsx15("h2", { children: "Privacy Policy" }),
          /* @__PURE__ */ jsxs6("p", { children: [
            /* @__PURE__ */ jsx15("strong", { children: "Effective Date:" }),
            " January 1st, 2026",
            /* @__PURE__ */ jsx15("br", {}),
            /* @__PURE__ */ jsx15("strong", { children: "Last Updated:" }),
            " March 18th, 2026"
          ] }),
          /* @__PURE__ */ jsx15("p", { children: "Legacy Orb LLC (\u201CLegacy Orb,\u201D \u201Cwe,\u201D \u201Cour,\u201D or \u201Cus\u201D) respects your privacy and is committed to protecting the personal information, stories, recordings, and other content you share with us through our website, mobile applications, and related services (collectively, the \u201CServices\u201D)." }),
          /* @__PURE__ */ jsx15("p", { children: "Legacy Orb is designed to help users capture, preserve, organize, and transform life stories, memories, and personal history into meaningful legacy content. Because the Services may involve highly personal materials, including autobiographical information, voice recordings, photos, videos, and AI-generated outputs, we aim to be transparent about what we collect, how we use it, and the choices available to you." }),
          /* @__PURE__ */ jsx15("p", { children: "By using our Services, you acknowledge that you have read and understood this Privacy Policy." }),
          /* @__PURE__ */ jsx15("h3", { children: "1. Scope of This Policy" }),
          /* @__PURE__ */ jsx15("p", { children: "This Privacy Policy applies to information collected through the Services, including information:" }),
          /* @__PURE__ */ jsxs6("ul", { children: [
            /* @__PURE__ */ jsx15("li", { children: "you provide directly to us," }),
            /* @__PURE__ */ jsx15("li", { children: "collected automatically when you use the Services, and" }),
            /* @__PURE__ */ jsx15("li", { children: "generated, inferred, or structured through AI-based processing." })
          ] }),
          /* @__PURE__ */ jsx15("p", { children: "This Policy applies regardless of the device or platform you use to access the Services." }),
          /* @__PURE__ */ jsx15("h3", { children: "2. Information We Collect" }),
          /* @__PURE__ */ jsx15("h4", { children: "2.1 Information You Provide" }),
          /* @__PURE__ */ jsx15("p", { children: "We may collect personal information you provide when you create an account, communicate with us, or use the Services, including:" }),
          /* @__PURE__ */ jsxs6("ul", { children: [
            /* @__PURE__ */ jsx15("li", { children: "your name," }),
            /* @__PURE__ */ jsx15("li", { children: "email address," }),
            /* @__PURE__ */ jsx15("li", { children: "login or account credentials," }),
            /* @__PURE__ */ jsx15("li", { children: "profile information," }),
            /* @__PURE__ */ jsx15("li", { children: "preferences, and" }),
            /* @__PURE__ */ jsx15("li", { children: "communications you send to us." })
          ] }),
          /* @__PURE__ */ jsx15("h4", { children: "2.2 Story Content and User-Generated Materials" }),
          /* @__PURE__ */ jsx15("p", { children: "Legacy Orb's core functionality involves collecting and processing content you choose to submit, which may include:" }),
          /* @__PURE__ */ jsxs6("ul", { children: [
            /* @__PURE__ */ jsx15("li", { children: "written responses to prompts and questions," }),
            /* @__PURE__ */ jsx15("li", { children: "journal entries and stories," }),
            /* @__PURE__ */ jsx15("li", { children: "voice recordings and transcripts," }),
            /* @__PURE__ */ jsx15("li", { children: "photos, videos, and other media files," }),
            /* @__PURE__ */ jsx15("li", { children: "descriptions of personal experiences, relationships, milestones, and life events." })
          ] }),
          /* @__PURE__ */ jsx15("p", { children: "This content may contain highly personal or sensitive information, and you choose what to submit." }),
          /* @__PURE__ */ jsx15("h4", { children: "2.3 Derived, Structured, and AI-Generated Data" }),
          /* @__PURE__ */ jsx15("p", { children: "In order to provide the Services, we may create additional data derived from your content, including:" }),
          /* @__PURE__ */ jsxs6("ul", { children: [
            /* @__PURE__ */ jsx15("li", { children: "categorized memory objects," }),
            /* @__PURE__ */ jsx15("li", { children: "tags, labels, and classifications," }),
            /* @__PURE__ */ jsx15("li", { children: "emotional, thematic, and contextual metadata," }),
            /* @__PURE__ */ jsx15("li", { children: "timelines and relationship structures," }),
            /* @__PURE__ */ jsx15("li", { children: "summaries, narratives, scripts, and other AI-generated outputs." })
          ] }),
          /* @__PURE__ */ jsx15("p", { children: "This derived data may be stored alongside your original content and used to improve the usefulness and organization of your legacy materials." }),
          /* @__PURE__ */ jsx15("h4", { children: "2.4 Technical and Usage Information" }),
          /* @__PURE__ */ jsx15("p", { children: "We may automatically collect certain technical and usage information, such as:" }),
          /* @__PURE__ */ jsxs6("ul", { children: [
            /* @__PURE__ */ jsx15("li", { children: "IP address," }),
            /* @__PURE__ */ jsx15("li", { children: "device type and operating system," }),
            /* @__PURE__ */ jsx15("li", { children: "browser type," }),
            /* @__PURE__ */ jsx15("li", { children: "app or site interaction logs," }),
            /* @__PURE__ */ jsx15("li", { children: "session duration," }),
            /* @__PURE__ */ jsx15("li", { children: "activity patterns," }),
            /* @__PURE__ */ jsx15("li", { children: "crash reports, diagnostics, and related analytics." })
          ] }),
          /* @__PURE__ */ jsx15("p", { children: "We use this information to operate, secure, troubleshoot, and improve the Services." }),
          /* @__PURE__ */ jsx15("h3", { children: "3. How We Use Information" }),
          /* @__PURE__ */ jsx15("p", { children: "We may use the information we collect to:" }),
          /* @__PURE__ */ jsxs6("ul", { children: [
            /* @__PURE__ */ jsx15("li", { children: "provide, operate, maintain, and support the Services," }),
            /* @__PURE__ */ jsx15("li", { children: "facilitate journaling, storytelling, and narrative generation," }),
            /* @__PURE__ */ jsx15("li", { children: "organize and structure submitted content into meaningful outputs," }),
            /* @__PURE__ */ jsx15("li", { children: "personalize prompts, follow-up questions, and user experience," }),
            /* @__PURE__ */ jsx15("li", { children: "improve system accuracy, performance, and product features," }),
            /* @__PURE__ */ jsx15("li", { children: "respond to support requests and user communications," }),
            /* @__PURE__ */ jsx15("li", { children: "detect, prevent, and address fraud, abuse, misuse, and security incidents," }),
            /* @__PURE__ */ jsx15("li", { children: "comply with legal obligations and enforce our terms." })
          ] }),
          /* @__PURE__ */ jsx15("h3", { children: "4. AI-Based Processing" }),
          /* @__PURE__ */ jsx15("p", { children: "Legacy Orb uses artificial intelligence and machine learning tools to help analyze user input, identify patterns and gaps in stories, organize content, and generate structured outputs such as summaries, narratives, scripts, and other legacy materials." }),
          /* @__PURE__ */ jsx15("p", { children: "By using the Services, you understand and agree that:" }),
          /* @__PURE__ */ jsxs6("ul", { children: [
            /* @__PURE__ */ jsx15("li", { children: "your submitted content may be processed by automated systems," }),
            /* @__PURE__ */ jsx15("li", { children: "your content may be transformed into derived outputs," }),
            /* @__PURE__ */ jsx15("li", { children: "we may use third-party AI and cloud infrastructure providers to help deliver the Services." })
          ] }),
          /* @__PURE__ */ jsx15("p", { children: "AI-generated outputs may not always be accurate, complete, or objectively correct. They may reflect inference, interpretation, summarization, or formatting based on the information provided. You are responsible for reviewing outputs before relying on or sharing them." }),
          /* @__PURE__ */ jsx15("h3", { children: "5. Legal Bases for Processing" }),
          /* @__PURE__ */ jsx15("p", { children: "Where applicable under relevant law, including certain international privacy frameworks, we may process personal information on one or more of the following bases:" }),
          /* @__PURE__ */ jsxs6("ul", { children: [
            /* @__PURE__ */ jsx15("li", { children: "your consent," }),
            /* @__PURE__ */ jsx15("li", { children: "performance of a contract," }),
            /* @__PURE__ */ jsx15("li", { children: "our legitimate interests," }),
            /* @__PURE__ */ jsx15("li", { children: "compliance with legal obligations." })
          ] }),
          /* @__PURE__ */ jsx15("h3", { children: "6. Data Sharing and Disclosure" }),
          /* @__PURE__ */ jsxs6("p", { children: [
            "We do ",
            /* @__PURE__ */ jsx15("strong", { children: "not" }),
            " sell personal data."
          ] }),
          /* @__PURE__ */ jsx15("p", { children: "We may disclose information in the following circumstances:" }),
          /* @__PURE__ */ jsx15("h4", { children: "6.1 Service Providers" }),
          /* @__PURE__ */ jsx15("p", { children: "We may use third-party vendors and service providers to support hosting, infrastructure, analytics, communications, AI processing, storage, authentication, customer support, and related business operations. These providers may process information on our behalf subject to contractual or legal safeguards." }),
          /* @__PURE__ */ jsx15("h4", { children: "6.2 Legal Compliance" }),
          /* @__PURE__ */ jsx15("p", { children: "We may disclose information where required to comply with law, regulation, subpoena, court order, legal process, or lawful government request." }),
          /* @__PURE__ */ jsx15("h4", { children: "6.3 Protection of Rights and Safety" }),
          /* @__PURE__ */ jsx15("p", { children: "We may disclose information when reasonably necessary to protect the rights, property, safety, users, or integrity of Legacy Orb, our Services, or others." }),
          /* @__PURE__ */ jsx15("h4", { children: "6.4 Business Transactions" }),
          /* @__PURE__ */ jsx15("p", { children: "We may disclose or transfer information in connection with a merger, acquisition, financing, restructuring, sale of assets, or other corporate transaction." }),
          /* @__PURE__ */ jsx15("h3", { children: "7. Data Storage, Security, and Safeguards" }),
          /* @__PURE__ */ jsx15("p", { children: "We implement commercially reasonable administrative, technical, and organizational safeguards designed to protect your information. These measures may include:" }),
          /* @__PURE__ */ jsxs6("ul", { children: [
            /* @__PURE__ */ jsx15("li", { children: "encryption in transit," }),
            /* @__PURE__ */ jsx15("li", { children: "encryption at rest," }),
            /* @__PURE__ */ jsx15("li", { children: "secure authentication controls," }),
            /* @__PURE__ */ jsx15("li", { children: "role-based access controls," }),
            /* @__PURE__ */ jsx15("li", { children: "monitoring and logging," }),
            /* @__PURE__ */ jsx15("li", { children: "access limitation based on operational need." })
          ] }),
          /* @__PURE__ */ jsx15("p", { children: "No system can be guaranteed to be completely secure. While we take reasonable steps to protect your information, we cannot guarantee absolute security." }),
          /* @__PURE__ */ jsx15("h3", { children: "8. Data Retention" }),
          /* @__PURE__ */ jsx15("p", { children: "We retain personal data for as long as reasonably necessary to:" }),
          /* @__PURE__ */ jsxs6("ul", { children: [
            /* @__PURE__ */ jsx15("li", { children: "provide the Services," }),
            /* @__PURE__ */ jsx15("li", { children: "maintain your account," }),
            /* @__PURE__ */ jsx15("li", { children: "fulfill contractual obligations," }),
            /* @__PURE__ */ jsx15("li", { children: "resolve disputes," }),
            /* @__PURE__ */ jsx15("li", { children: "enforce our agreements," }),
            /* @__PURE__ */ jsx15("li", { children: "comply with legal obligations," }),
            /* @__PURE__ */ jsx15("li", { children: "support backup, recovery, fraud prevention, and system integrity." })
          ] }),
          /* @__PURE__ */ jsx15("p", { children: "Because Legacy Orb is designed around preservation and long-term storytelling, some content may be stored in archival formats intended to support future access and legacy use, subject to this Policy and applicable law." }),
          /* @__PURE__ */ jsx15("h3", { children: "9. Data Deletion" }),
          /* @__PURE__ */ jsxs6("p", { children: [
            "You may request deletion of your personal data at any time by contacting us at",
            " ",
            /* @__PURE__ */ jsx15("a", { href: "mailto:admin@legacyorb.com", children: "admin@legacyorb.com" }),
            "."
          ] }),
          /* @__PURE__ */ jsx15("p", { children: "Upon a valid deletion request:" }),
          /* @__PURE__ */ jsxs6("ul", { children: [
            /* @__PURE__ */ jsx15("li", { children: "active data may be removed from production systems," }),
            /* @__PURE__ */ jsx15("li", { children: "backup or archival copies may remain for a limited period," }),
            /* @__PURE__ */ jsx15("li", { children: "certain data may be retained where required by law, for legitimate business purposes, or for security, fraud prevention, dispute resolution, and compliance reasons." })
          ] }),
          /* @__PURE__ */ jsx15("h3", { children: "10. Your Privacy Rights" }),
          /* @__PURE__ */ jsx15("p", { children: "Subject to applicable law, you may have the right to:" }),
          /* @__PURE__ */ jsxs6("ul", { children: [
            /* @__PURE__ */ jsx15("li", { children: "request access to your data," }),
            /* @__PURE__ */ jsx15("li", { children: "request correction of inaccurate information," }),
            /* @__PURE__ */ jsx15("li", { children: "request deletion of your data," }),
            /* @__PURE__ */ jsx15("li", { children: "object to or restrict certain processing," }),
            /* @__PURE__ */ jsx15("li", { children: "request portability of eligible data." })
          ] }),
          /* @__PURE__ */ jsxs6("p", { children: [
            "To exercise these rights, contact us at",
            " ",
            /* @__PURE__ */ jsx15("a", { href: "mailto:admin@legacyorb.com", children: "admin@legacyorb.com" }),
            ". We may need to verify your identity before processing certain requests."
          ] }),
          /* @__PURE__ */ jsx15("h3", { children: "11. Content Ownership and License" }),
          /* @__PURE__ */ jsx15("p", { children: "You retain ownership of the content you submit to Legacy Orb." }),
          /* @__PURE__ */ jsx15("p", { children: "However, by submitting content through the Services, you grant Legacy Orb a non-exclusive, worldwide, royalty-free license to host, store, process, analyze, reproduce, transform, and generate derivative outputs from that content as necessary to operate, improve, and provide the Services. This includes the creation of summaries, narratives, story structures, scripts, and related outputs." }),
          /* @__PURE__ */ jsx15("h3", { children: "12. Sensitive Information" }),
          /* @__PURE__ */ jsx15("p", { children: "The Services may involve the submission of highly personal or sensitive information. Submission of such information is voluntary." }),
          /* @__PURE__ */ jsx15("p", { children: "By using the Services, you acknowledge that:" }),
          /* @__PURE__ */ jsxs6("ul", { children: [
            /* @__PURE__ */ jsx15("li", { children: "you choose what content to submit," }),
            /* @__PURE__ */ jsx15("li", { children: "you are responsible for the information you provide," }),
            /* @__PURE__ */ jsx15("li", { children: "Legacy Orb does not independently verify the factual accuracy of user-submitted content." })
          ] }),
          /* @__PURE__ */ jsx15("h3", { children: "13. Children's Privacy" }),
          /* @__PURE__ */ jsx15("p", { children: "The Services are not intended for children under 18, or under any higher minimum age required by applicable law. We do not knowingly collect personal information from children in violation of applicable law. If you believe a child has provided personal information to us improperly, please contact us so we can take appropriate action." }),
          /* @__PURE__ */ jsx15("h3", { children: "14. International Data Transfers" }),
          /* @__PURE__ */ jsx15("p", { children: "Your information may be transferred to, stored in, or processed in countries other than the one in which you reside, including the United States. By using the Services, you understand that your information may be processed in jurisdictions that may have different data protection laws than your home jurisdiction." }),
          /* @__PURE__ */ jsx15("h3", { children: "15. Digital Legacy and Inheritance" }),
          /* @__PURE__ */ jsx15("p", { children: "Legacy Orb recognizes that user content may have enduring personal, familial, and historical value. This section explains how we may handle content in connection with death, incapacity, memorialization, or future legacy-related features." }),
          /* @__PURE__ */ jsx15("h4", { children: "15.1 Ownership During Lifetime" }),
          /* @__PURE__ */ jsx15("p", { children: "During your lifetime, you retain ownership of your content, subject to the license granted in this Privacy Policy." }),
          /* @__PURE__ */ jsx15("p", { children: "Where functionality is available, you may be able to indicate preferences regarding:" }),
          /* @__PURE__ */ jsxs6("ul", { children: [
            /* @__PURE__ */ jsx15("li", { children: "whether your content remains private," }),
            /* @__PURE__ */ jsx15("li", { children: "whether designated individuals may access certain content," }),
            /* @__PURE__ */ jsx15("li", { children: "whether your content may be transformed into legacy outputs such as books, films, or archives." })
          ] }),
          /* @__PURE__ */ jsx15("h4", { children: "15.2 Legacy Contacts" }),
          /* @__PURE__ */ jsx15("p", { children: "Legacy Orb may, in the future, allow users to designate one or more legacy contacts or beneficiaries who may be eligible to access certain content after verification of death or incapacity. Any such feature, if offered, may include limits on access type, permissions, and transferability." }),
          /* @__PURE__ */ jsx15("h4", { children: "15.3 Posthumous Access" }),
          /* @__PURE__ */ jsx15("p", { children: "If a user dies, Legacy Orb may, subject to applicable law and satisfactory verification, consider requests from designated legacy contacts, lawful heirs, executors, or authorized representatives. We may require documents such as a death certificate, court order, or proof of authority before taking action. We are not obligated to grant access without sufficient verification or legal basis." }),
          /* @__PURE__ */ jsx15("h4", { children: "15.4 No Instructions on File" }),
          /* @__PURE__ */ jsx15("p", { children: "If no legacy instructions or designated contacts exist, content may remain private and inaccessible except where access is required by law or supported by valid legal authority." }),
          /* @__PURE__ */ jsx15("h4", { children: "15.5 Preservation and Archiving" }),
          /* @__PURE__ */ jsx15("p", { children: "We may store certain user content in long-term archival formats intended to preserve stories, recordings, images, videos, and structured life-story data. However, we do not guarantee perpetual storage, indefinite retention, or uninterrupted future accessibility." }),
          /* @__PURE__ */ jsx15("h4", { children: "15.6 Rights of Heirs and Representatives" }),
          /* @__PURE__ */ jsx15("p", { children: "To the extent permitted by law, heirs, executors, or authorized representatives may request access, deletion, or export of eligible content, subject to verification, privacy protections, third-party rights, and technical limitations." }),
          /* @__PURE__ */ jsx15("h4", { children: "15.7 Privacy of Third Parties" }),
          /* @__PURE__ */ jsx15("p", { children: "Your content may include references to family members, friends, and other individuals. We may take reasonable measures to protect third-party privacy and limit disclosure where appropriate." }),
          /* @__PURE__ */ jsx15("h4", { children: "15.8 Future Legacy Features" }),
          /* @__PURE__ */ jsx15("p", { children: "Legacy Orb may introduce additional digital legacy features over time, such as memorial account settings, time-released messages, beneficiary access tools, or legacy instructions. If we do, we may update this Privacy Policy accordingly." }),
          /* @__PURE__ */ jsx15("h3", { children: "16. Changes to This Privacy Policy" }),
          /* @__PURE__ */ jsx15("p", { children: "We may update this Privacy Policy from time to time. If we make material changes, we may post the updated version on this page and revise the \u201CLast Updated\u201D date above. Your continued use of the Services after changes become effective constitutes acceptance of the updated Privacy Policy, to the extent permitted by law." }),
          /* @__PURE__ */ jsx15("h3", { children: "Contact Us" }),
          /* @__PURE__ */ jsx15("p", { children: "If you have questions about this Privacy Policy or would like to exercise your privacy rights:" }),
          /* @__PURE__ */ jsxs6("p", { children: [
            "Legacy Orb LLC",
            /* @__PURE__ */ jsx15("br", {}),
            /* @__PURE__ */ jsx15("a", { href: "mailto:Info@LegacyOrb.com", children: "Info@LegacyOrb.com" }),
            /* @__PURE__ */ jsx15("br", {}),
            "(703) 679-8707"
          ] })
        ]
      }
    )
  ] });
}
export {
  AuthCard,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  FormField,
  Input,
  Label,
  PasswordInput,
  PrivacyContent,
  Separator,
  Skeleton,
  SubmitButton,
  TermsContent,
  Textarea,
  VerificationCodeInput,
  badgeVariants,
  buttonVariants,
  cn
};
